import { For, Index, Show, createMemo, createSignal } from "solid-js";
import { Portal } from "solid-js/web";
import type { SegmentUnits, VoyageStopOverride } from "../../../../backend/shared";
import { useModalDismiss } from "../../shared/hooks/useModalDismiss";
import s from "./StopOverrideDialog.module.css";
import {
  bumpDate,
  bumpDateField,
  bumpTime,
  colHasOverride,
  createWheelStepper,
  dateFieldAtOffset,
  measureCharWidth,
  nextSegValue,
  rowHasOverride,
  segIsAtDefault,
  SEGMENT_CODES,
  segStateFrom,
  segStateToSegments,
  timeFieldAtOffset,
  validateSessions,
  type SegmentCode,
  type SegmentState,
} from "./stevedore-sessions";
import { DateField } from "../../shared/inputs/DateField";
import { TimeField } from "../../shared/inputs/TimeField";

export interface StopOverrideDefaults {
  // Defaults shown as placeholders when the user hasn't entered an
  // override. Stevedore window defaults to the stop's arrival/departure;
  // unit defaults are pulled from cargoData by the caller.
  arrivalLocal: string | null;
  departureLocal: string | null;
  loadUnits: number | null;
  dischargeUnits: number | null;
  shiftingUnits: number | null;
  // Per-segment defaults (canonical C / HH / RT / BB / NCC keys)
  // rendered as placeholders inside each grid input. Caller maps the
  // upstream cargoData segments through `segmentCode` and sums.
  loadSegments?: SegmentUnits | null;
  dischargeSegments?: SegmentUnits | null;
  shiftingSegments?: SegmentUnits | null;
}

export interface StopOverrideDialogProps {
  vesselCode: string;
  voyageNo: string;
  stopOrder: number;
  // Discriminator for upstream dummy stops (anchors borrowed from the
  // previous / next voyage). "" for real stops; the override row's PK
  // is (vessel, voyageNo, stopOrder, dummySource) so a real stop and a
  // dummy at the same order don't collide.
  dummySource: "" | "previous" | "next";
  stopLabel: string;             // e.g. "BEANR — ANTWERP TERMINAL"
  defaults: StopOverrideDefaults;
  current: VoyageStopOverride | null;
  onSave: (row: VoyageStopOverride) => Promise<void>;
  onClose: () => void;
}

function isoToMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

export function StopOverrideDialog(props: StopOverrideDialogProps) {
  // Bounds for anchoring sessions to a calendar date come from the
  // vessel's at-terminal window. When unavailable we can't construct
  // ISO datetimes for sessions so the UI hides the editor.
  const minMs = createMemo(() => isoToMs(props.defaults.arrivalLocal));
  const maxMs = createMemo(() => isoToMs(props.defaults.departureLocal));
  const hasBounds = createMemo(() => {
    const lo = minMs();
    const hi = maxMs();
    return lo !== null && hi !== null && hi > lo;
  });

  type SessionDraft = {
    startDate: string;  // YYYY-MM-DD
    startTime: string;  // HH:MM
    endDate: string;
    endTime: string;
  };

  function dateFromIso(iso: string): string {
    return iso.length >= 10 ? iso.slice(0, 10) : "";
  }
  function timeFromIso(iso: string): string {
    return iso.length >= 16 ? iso.slice(11, 16) : "";
  }

  function sessionsFromCurrent(): SessionDraft[] {
    if (props.current?.stevedoreSessions && props.current.stevedoreSessions.length > 0) {
      return props.current.stevedoreSessions.map((sess) => ({
        startDate: dateFromIso(sess.start),
        startTime: timeFromIso(sess.start),
        endDate: dateFromIso(sess.end),
        endTime: timeFromIso(sess.end),
      }));
    }
    return [];
  }

  const [sessions, setSessions] = createSignal<SessionDraft[]>(sessionsFromCurrent());

  function updateSessionField(idx: number, field: keyof SessionDraft, value: string) {
    setSessions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [field]: value };
      return next;
    });
  }

  // Wheel-to-step on the date / time fields. Accumulator damps trackpads'
  // many-tiny-events-per-gesture down to one ±1 step per ~50px deltaY;
  // preventDefault + stopPropagation so the dialog body doesn't scroll
  // alongside the value adjustment.
  const wheelStep = createWheelStepper(50);

  // The wheel handler hangs off the wrapper div (which catches wheels
  // over the input and over the flex slack alike). Field-zone detection
  // needs the cursor's position relative to the actual <input> rect,
  // not the wrapper — the input's text is centred inside the wrapper,
  // and the slack to either side would skew an offsetX-based read.
  function onDateWheel(
    idx: number,
    field: "startDate" | "endDate",
    current: string,
    e: WheelEvent,
  ) {
    e.preventDefault();
    e.stopPropagation();
    const host = e.currentTarget as HTMLElement;
    const dir = wheelStep(host, e.deltaY);
    if (dir === 0) return;
    const input = host.querySelector("input");
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const font = window.getComputedStyle(input).font;
    const charW = measureCharWidth(font);
    const part = dateFieldAtOffset(x, rect.width, charW);
    updateSessionField(idx, field, bumpDateField(current, dir, part));
  }

  function onTimeWheel(
    idx: number,
    timeField: "startTime" | "endTime",
    dateField: "startDate" | "endDate",
    currentTime: string,
    currentDate: string,
    e: WheelEvent,
  ) {
    e.preventDefault();
    e.stopPropagation();
    const host = e.currentTarget as HTMLElement;
    const dir = wheelStep(host, e.deltaY);
    if (dir === 0) return;
    const input = host.querySelector("input");
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const side = timeFieldAtOffset(x, rect.width);
    const { time, dayDelta } = bumpTime(currentTime, dir, side);
    setSessions((prev) => {
      const nextRows = [...prev];
      const row = { ...nextRows[idx]!, [timeField]: time };
      if (dayDelta !== 0 && currentDate) {
        row[dateField] = bumpDate(currentDate, dayDelta);
      }
      nextRows[idx] = row;
      return nextRows;
    });
  }

  // Per-segment state — { C, HH, RT, BB, NCC } maps for each side.
  // Initialised from the existing override's segment maps (winning)
  // then from the upstream cargo default. Pre-fill makes the native
  // spinner / wheel / arrow keys work on the very first interaction;
  // segStateToSegments drops cells still equal to the default at save
  // time so no-op overrides aren't persisted.
  const [loadSeg, setLoadSeg] = createSignal<SegmentState>(
    segStateFrom(props.current?.loadSegments, props.defaults.loadSegments),
  );
  const [dischSeg, setDischSeg] = createSignal<SegmentState>(
    segStateFrom(props.current?.dischargeSegments, props.defaults.dischargeSegments),
  );
  const [shiftSeg, setShiftSeg] = createSignal<SegmentState>(
    segStateFrom(props.current?.shiftingSegments, props.defaults.shiftingSegments),
  );
  function setSeg(
    setter: (next: SegmentState | ((prev: SegmentState) => SegmentState)) => void,
    code: SegmentCode,
    raw: string,
    defaults: SegmentUnits | null | undefined,
  ): void {
    const next = nextSegValue(raw, defaults, code);
    setter((prev: SegmentState) => ({ ...prev, [code]: next }));
  }
  // Only suppress the native wheel-to-step when the number input is
  // actually focused — Chrome doesn't mutate an unfocused number input
  // on wheel anyway, so blanket-blocking wheel kills the dialog body's
  // scroll over the entire volume grid (which is what we want to
  // protect against, not propagate).
  function onSegWheel(e: WheelEvent): void {
    if (document.activeElement === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  // ESC closes; focus returns to whatever opened the dialog (typically
  // the stop's pencil button in ItinerarySection).
  useModalDismiss({ onClose: () => props.onClose(), restoreFocus: true });

  async function commit(row: VoyageStopOverride) {
    setSaving(true);
    setError(null);
    try {
      await props.onSave(row);
      props.onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function onSave() {
    const result = validateSessions(sessions());
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const serialisedSessions = result.sessions;

    void commit({
      vesselCode: props.vesselCode,
      voyageNo: props.voyageNo,
      stopOrder: props.stopOrder,
      dummySource: props.dummySource,
      stevedoreSessions: serialisedSessions,
      // Legacy per-side totals: null in the new flow; the per-segment
      // maps below carry the value, and readers sum them when they
      // need a total. Kept on the type for backward-compat with rows
      // stored before the segment maps landed.
      loadUnits: null,
      dischargeUnits: null,
      shiftingUnits: null,
      loadSegments: segStateToSegments(loadSeg(), props.defaults.loadSegments),
      dischargeSegments: segStateToSegments(dischSeg(), props.defaults.dischargeSegments),
      shiftingSegments: segStateToSegments(shiftSeg(), props.defaults.shiftingSegments),
    });
  }

  return (
    <Portal>
      <div class={s.backdrop} onMouseDown={() => props.onClose()}>
        <div
          class={s.card}
          role="dialog"
          aria-modal="true"
          aria-labelledby="stop-override-title"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <header class={s.head}>
            <div class={s.headText}>
              <span class={s.kind}>Stop overrides</span>
              <h3 id="stop-override-title" class={s.title}>{props.stopLabel}</h3>
            </div>
            <button type="button" class={s.close} aria-label="Close" onClick={() => props.onClose()}>×</button>
          </header>
          <div class={s.body}>
            <fieldset class={s.group}>
              <legend>Stevedore working windows</legend>
              <Show
                when={hasBounds()}
                fallback={
                  <div class={s.hint}>
                    Vessel arrival / departure unknown — can't set stevedore windows.
                  </div>
                }
              >
                <div class={s.sessionsList}>
                  <Index each={sessions()}>
                    {(session, i) => (
                      <div class={s.sessionRow}>
                        <div class={s.sessionDateTimePair}>
                          <div
                            class={s.sessionDateInput}
                            onWheel={(e) => onDateWheel(i, "startDate", session().startDate, e)}
                          >
                            <DateField
                              value={() => session().startDate}
                              setValue={(iso) => updateSessionField(i, "startDate", iso)}
                              ariaLabel={`Session ${i + 1} start date`}
                            />
                          </div>
                          <div
                            class={s.sessionInput}
                            onWheel={(e) =>
                              onTimeWheel(
                                i,
                                "startTime",
                                "startDate",
                                session().startTime,
                                session().startDate,
                                e,
                              )
                            }
                          >
                            <TimeField
                              value={() => session().startTime}
                              setValue={(t) => updateSessionField(i, "startTime", t)}
                              ariaLabel={`Session ${i + 1} start time`}
                            />
                          </div>
                        </div>
                        <span class={s.sessionSep}>→</span>
                        <div class={s.sessionDateTimePair}>
                          <div
                            class={s.sessionDateInput}
                            onWheel={(e) => onDateWheel(i, "endDate", session().endDate, e)}
                          >
                            <DateField
                              value={() => session().endDate}
                              setValue={(iso) => updateSessionField(i, "endDate", iso)}
                              ariaLabel={`Session ${i + 1} end date`}
                            />
                          </div>
                          <div
                            class={s.sessionInput}
                            onWheel={(e) =>
                              onTimeWheel(
                                i,
                                "endTime",
                                "endDate",
                                session().endTime,
                                session().endDate,
                                e,
                              )
                            }
                          >
                            <TimeField
                              value={() => session().endTime}
                              setValue={(t) => updateSessionField(i, "endTime", t)}
                              ariaLabel={`Session ${i + 1} end time`}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          class={s.sessionRemove}
                          aria-label={`Remove session ${i + 1}`}
                          onClick={() =>
                            setSessions((prev) => prev.filter((_, idx) => idx !== i))
                          }
                        >×</button>
                      </div>
                    )}
                  </Index>
                  <button
                    type="button"
                    class={s.sessionAdd}
                    onClick={() => {
                      const arrISO = props.defaults.arrivalLocal;
                      const depISO = props.defaults.departureLocal;
                      setSessions((prev) => [
                        ...prev,
                        {
                          startDate: arrISO ? dateFromIso(arrISO) : "",
                          startTime: arrISO ? timeFromIso(arrISO) : "",
                          endDate: depISO ? dateFromIso(depISO) : (arrISO ? dateFromIso(arrISO) : ""),
                          endTime: depISO ? timeFromIso(depISO) : "",
                        },
                      ]);
                    }}
                  >+ Add session</button>
                </div>
              </Show>
            </fieldset>
            <fieldset class={s.group}>
              <legend>Volume overrides (units)</legend>
              <div class={s.volumeGrid}>
                <div class={`${s.volumeHeader} ${s.volumeRowLabel}`} />
                {SEGMENT_CODES.map((code) => (
                  <div class={`${s.volumeHeader} ${
                    colHasOverride(
                      code,
                      loadSeg(),  props.defaults.loadSegments,
                      dischSeg(), props.defaults.dischargeSegments,
                      shiftSeg(), props.defaults.shiftingSegments,
                    ) ? s.volumeHeaderActive : ""
                  }`}>{code}</div>
                ))}
                <div class={`${s.volumeRowLabel} ${rowHasOverride(loadSeg(), props.defaults.loadSegments) ? s.volumeRowLabelActive : ""}`}>Load</div>
                <For each={SEGMENT_CODES}>{(code) => (
                  <div class={s.volumeCell}>
                    <input
                      type="number"
                      min="0"
                      class={`${s.volumeInput} ${segIsAtDefault(loadSeg(), props.defaults.loadSegments, code) ? s.volumeInputDefault : ""}`}
                      value={loadSeg()[code] === "" ? "" : String(loadSeg()[code])}
                      onInput={(e) => setSeg(setLoadSeg, code, e.currentTarget.value, props.defaults.loadSegments)}
                      onWheel={onSegWheel}
                      aria-label={`Load ${code}`}
                    />
                  </div>
                )}</For>
                <div class={`${s.volumeRowLabel} ${rowHasOverride(dischSeg(), props.defaults.dischargeSegments) ? s.volumeRowLabelActive : ""}`}>Discharge</div>
                <For each={SEGMENT_CODES}>{(code) => (
                  <div class={s.volumeCell}>
                    <input
                      type="number"
                      min="0"
                      class={`${s.volumeInput} ${segIsAtDefault(dischSeg(), props.defaults.dischargeSegments, code) ? s.volumeInputDefault : ""}`}
                      value={dischSeg()[code] === "" ? "" : String(dischSeg()[code])}
                      onInput={(e) => setSeg(setDischSeg, code, e.currentTarget.value, props.defaults.dischargeSegments)}
                      onWheel={onSegWheel}
                      aria-label={`Discharge ${code}`}
                    />
                  </div>
                )}</For>
                <div class={`${s.volumeRowLabel} ${rowHasOverride(shiftSeg(), props.defaults.shiftingSegments) ? s.volumeRowLabelActive : ""}`}>Shifting</div>
                <For each={SEGMENT_CODES}>{(code) => (
                  <div class={s.volumeCell}>
                    <input
                      type="number"
                      min="0"
                      class={`${s.volumeInput} ${segIsAtDefault(shiftSeg(), props.defaults.shiftingSegments, code) ? s.volumeInputDefault : ""}`}
                      value={shiftSeg()[code] === "" ? "" : String(shiftSeg()[code])}
                      onInput={(e) => setSeg(setShiftSeg, code, e.currentTarget.value, props.defaults.shiftingSegments)}
                      onWheel={onSegWheel}
                      aria-label={`Shifting ${code}`}
                    />
                  </div>
                )}</For>
              </div>
            </fieldset>
            <Show when={error()}>
              <div class={s.error}>{error()}</div>
            </Show>
          </div>
          <footer class={s.actions}>
            <div class={s.actionsRight}>
              <button type="button" class={s.cancel} onClick={() => props.onClose()} disabled={saving()}>Cancel</button>
              <button type="button" class={s.save} onClick={onSave} disabled={saving()}>
                {saving() ? "Saving…" : "Save"}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </Portal>
  );
}

